import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Table, Pagination, Select } from "components/ui";
import CustomCheckbox from "components/shared/CustomCheckbox";
import TableRowSkeleton from "./loaders/TableRowSkeleton";
import Loading from "./Loading";
import { useTable, usePagination, useSortBy, useRowSelect } from "react-table";

const { Tr, Th, Td, THead, TBody, Sorter } = Table;

const tableStyles = `
  .dt-table { width: 100%; overflow-x: auto; }

  .dt-table table {
    border-collapse: collapse !important;
    border-spacing: 0 !important;
    border: none !important;
    width: 100%;
    background: transparent !important;
  }

  .dt-table tr {
    border: none !important;
    box-shadow: none !important;
    outline: none !important;
    background: transparent !important;
  }

  .dt-table thead tr {
    border: none !important;
    background: transparent !important;
    height: 34px !important;
  }

  .dt-table thead th {
    border-top: 1px solid #ECF0F6 !important;
    border-left: 1px solid #ECF0F6 !important;
    border-right: 1px solid #ECF0F6 !important;
    border-bottom: 1px solid #ECF0F6 !important;
    box-shadow: none !important;
    outline: none !important;
    padding: 6px 16px !important;
    height: 34px !important;

    font-family: Inter, sans-serif !important;
    text-transform: initial !important;
    font-weight: 600 !important;
    font-size: 13px !important;
    line-height: 100% !important;
    letter-spacing: 0 !important;
    color: #3C4657 !important;
    vertical-align: middle !important;
    background: transparent !important;
    white-space: nowrap !important;
  }

  .dt-table thead th:first-child {
    border-left: none !important;
  }

  .dt-table tbody tr {
    border: none !important;
    background: transparent !important;
    height: 34px !important;
  }

  .dt-table tbody tr:hover {
    background: transparent !important;
  }

  .dt-table tbody tr:hover td {
    background: transparent !important;
  }

  .dt-table tbody td {
    border-top: 1px solid #ECF0F6 !important;
    border-left: 1px solid #ECF0F6 !important;
    border-right: 1px solid #ECF0F6 !important;
    border-bottom: 1px solid #ECF0F6 !important;
    box-shadow: none !important;
    outline: none !important;
    padding: 0px 16px !important;
    height: 34px !important;
    font-family: Inter, sans-serif !important;
    font-weight: 400 !important;
    font-size: 13px !important;
    line-height: 100% !important;
    letter-spacing: 0 !important;
    color: #3C4657 !important;
    vertical-align: middle !important;
    background: transparent !important;
    white-space: nowrap !important;
  }

  .dt-table tbody td:first-child {
    border-left: none !important;
  }

  .dt-table .dt-empty-row {
    border: none !important;
    background: transparent !important;
    height: 34px !important;
  }

  .dt-table .dt-empty-cell {
    border-top: 1px solid #ECF0F6 !important;
    border-bottom: 1px solid #ECF0F6 !important;
    border-left: 1px solid #ECF0F6 !important;
    border-right: 1px solid #ECF0F6 !important;
    padding: 5px 16px !important;
    background: transparent !important;
    white-space: nowrap !important;
    font-size: 13px !important;
    line-height: 13px !important;
    font-family: Inter, sans-serif !important;
    vertical-align: middle !important;
    box-sizing: border-box !important;
  }

  .dt-table .dt-empty-cell > div {
    height: 22px;
    line-height: 22px;
  }

  .dt-table .dt-empty-cell:first-child {
    border-left: none !important;
  }
 `;

const IndeterminateCheckbox = ({ indeterminate, onChange, onCheckBoxChange, onIndeterminateCheckBoxChange, ...rest }) => {
  const handleChange = () => {
    const syntheticEvent = { target: { checked: !rest.checked } }
    onChange(syntheticEvent)
    onCheckBoxChange?.(syntheticEvent)
    onIndeterminateCheckBoxChange?.(syntheticEvent)
  }

  return (
    <CustomCheckbox
      checked={rest.checked}
      onChange={handleChange}
      indeterminate={indeterminate}
      className="mb-0"
    />
  )
}

const DataTable = (props) => {
  const {
    skeletonAvatarColumns,
    columns,
    data,
    loading,
    onCheckBoxChange,
    onIndeterminateCheckBoxChange,
    onPaginationChange,
    onSelectChange,
    onSort,
    pageSizes,
    selectable,
    skeletonAvatarProps,
    pagingData,
    autoResetSelectedRows,
  } = props;

  const { pageSize, pageIndex, total } = pagingData;

  const rangeStart = total === 0 ? 0 : (pageIndex - 1) * pageSize + 1;
  const rangeEnd = Math.min(pageIndex * pageSize, total);

  const pageSizeOption = useMemo(
    () => pageSizes.map((number) => ({ value: number, label: `${number}` })),
    [pageSizes],
  );

  const handleCheckBoxChange = (checked, row) => {
    if (!loading) onCheckBoxChange?.(checked, row);
  };

  const handleIndeterminateCheckBoxChange = (checked, rows) => {
    if (!loading) onIndeterminateCheckBoxChange?.(checked, rows);
  };

  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, page } =
    useTable(
      {
        columns,
        data,
        manualPagination: true,
        manualSortBy: true,
        autoResetSelectedRows,
      },
      useSortBy,
      usePagination,
      useRowSelect,
      (hooks) => {
        if (selectable) {
          hooks.visibleColumns.push((columns) => [
            {
              id: "selection",
              Header: (props) => (
                <div className="flex justify-center">
                  <IndeterminateCheckbox
                    {...props.getToggleAllRowsSelectedProps()}
                    onIndeterminateCheckBoxChange={(e) =>
                      handleIndeterminateCheckBoxChange(
                        e.target.checked,
                        props.rows,
                      )
                    }
                  />
                </div>
              ),
              Cell: ({ row }) => (
                <div className="flex justify-center">
                  <IndeterminateCheckbox
                    {...row.getToggleRowSelectedProps()}
                    onCheckBoxChange={(e) =>
                      handleCheckBoxChange(e.target.checked, row.original)
                    }
                  />
                </div>
              ),
              sortable: false,
            },
            ...columns,
          ]);
        }
      },
    );

  const handlePaginationChange = (page) => {
    if (!loading) onPaginationChange?.(page);
  };

  const handleSelectChange = (value) => {
    if (!loading) onSelectChange?.(Number(value));
  };

  const handleSort = (column) => {
    if (!loading) {
      const { id, isSortedDesc, toggleSortBy, clearSortBy } = column;
      const sortOrder = isSortedDesc ? "desc" : "asc";
      toggleSortBy(!isSortedDesc);
      onSort?.({ order: sortOrder, key: id }, { id, clearSortBy });
    }
  };

  const totalColumns = headerGroups[0]?.headers?.length || columns.length;

  return (
    <Loading loading={loading && data.length !== 0} type="cover">
      <style>{tableStyles}</style>

      {/* ── Table ── */}
      <div className="dt-table">
        <Table {...getTableProps()}>
          <THead>
            {headerGroups.map((headerGroup) => (
              <Tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <Th {...column.getHeaderProps()}>
                    {column.render("Header") &&
                      (column.sortable ? (
                        <div
                          className="cursor-pointer select-none flex items-center gap-1"
                          onClick={() => handleSort(column)}
                        >
                          {column.render("Header")}
                          <span>
                            <Sorter sort={column.isSortedDesc} />
                          </span>
                        </div>
                      ) : (
                        <div>{column.render("Header")}</div>
                      ))}
                  </Th>
                ))}
              </Tr>
            ))}
          </THead>

          {loading && data.length === 0 ? (
            <TableRowSkeleton
              columns={columns.length}
              rows={pagingData.pageSize}
              avatarInColumns={skeletonAvatarColumns}
              avatarProps={skeletonAvatarProps}
            />
          ) : (
            <TBody {...getTableBodyProps()}>
              {page.map((row) => {
                prepareRow(row);
                return (
                  <Tr {...row.getRowProps()}>
                    {row.cells.map((cell) => (
                      <Td {...cell.getCellProps()}>
                        {cell.render("Cell")}
                      </Td>
                    ))}
                  </Tr>
                );
              })}
              {Array.from({ length: Math.max(0, pageSize - page.length) }).map((_, i) => (
                <tr key={`empty-${i}`} className="dt-empty-row">
                  {Array.from({ length: totalColumns }).map((_, ci) => (
                    <td key={ci} className="dt-empty-cell"><div style={{ visibility: 'hidden' }}>&nbsp;</div></td>
                  ))}
                </tr>
              ))}
            </TBody>
          )}
        </Table>
      </div>

     {/* ── Footer ── */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
          padding: "14px 16px 6px",
          // borderTop: "1px solid #ECF0F6",
        }}
      >
        {/* Showing X from Y results */}
        <p
          style={{
            fontFamily: "Inter, sans-serif",
            fontSize: "13px",
            fontWeight: 400,
            color: "#6B7280",
            whiteSpace: "nowrap",
            margin: 0,
            flexShrink: 0,
          }}
        >
          Showing{" "}
          <span style={{ fontWeight: 600, color: "#3C4657" }}>{rangeEnd}</span>
          {" "}from{" "}
          <span style={{ fontWeight: 600, color: "#3C4657" }}>{total}</span>
          {" "}results
        </p>

        {/* Per-page + Pagination */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "12px",
            flexShrink: 0,
          }}
        >
          <Select
            size="sm"
            menuPlacement="top"
            isSearchable={false}
            value={pageSizeOption.find((o) => o.value === pageSize)}
            options={pageSizeOption}
            onChange={(option) => onSelectChange?.(Number(option.value))}
          />
          <Pagination
            pageSize={pageSize}
            currentPage={pageIndex}
            total={total}
            onChange={handlePaginationChange}
          />
        </div>
      </div>
    </Loading>
  );
};

DataTable.propTypes = {
  columns: PropTypes.array,
  data: PropTypes.array,
  loading: PropTypes.bool,
  onCheckBoxChange: PropTypes.func,
  onIndeterminateCheckBoxChange: PropTypes.func,
  onPaginationChange: PropTypes.func,
  onSelectChange: PropTypes.func,
  onSort: PropTypes.func,
  pageSizes: PropTypes.arrayOf(PropTypes.number),
  selectable: PropTypes.bool,
  skeletonAvatarColumns: PropTypes.arrayOf(PropTypes.number),
  skeletonAvatarProps: PropTypes.object,
  pagingData: PropTypes.shape({
    total: PropTypes.number,
    pageIndex: PropTypes.number,
    pageSize: PropTypes.number,
  }),
  autoResetSelectedRows: PropTypes.bool,
};

DataTable.defaultProps = {
  pageSizes: [10, 25, 50, 100],
  pagingData: {
    total: 0,
    pageIndex: 1,
    pageSize: 10,
  },
  data: [],
  columns: [],
  selectable: false,
  loading: false,
  autoResetSelectedRows: true,
};

export default DataTable;